import React, { useState } from 'react';
import { RCButton, RCTextWithIcon } from '@reverbdotcom/cadence/components';
import { inExperiment, useUser } from '@reverbdotcom/commons/src/user_hooks';
import { MutationFn } from 'react-apollo';
import { HeartEmptyIcon, HeartFilledIcon } from '@reverbdotcom/cadence/icons/react';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import gql from 'graphql-tag';
import { DeleteMyFavoriteShop, FavoriteShopCTAData, UndoDeleteMyFavorite,
  UpsertMyFavoriteShop, core_apimessages_Favorite, core_apimessages_FavoriteType } from '../../schemas/rql';
import I18n from 'i18n-js';
import * as elog from '@reverbdotcom/commons/src/elog';
import experiments from '@reverbdotcom/commons/src/experiments';
import classNames from 'classnames';
import { useMutation, MutationResult } from '@reverbdotcom/commons/src/useMutation';
import { connect } from '@reverbdotcom/commons/src/connect';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { getStarsCount } from '@reverbdotcom/cadence/components';
import { resolveCurrentView, resolveFavoriteType } from '@reverbdotcom/commons/src/event_tracking/favorites';
import { ToastCloseType } from '@reverbdotcom/cadence/components/types';
import { useURL } from '@reverbdotcom/commons/src/url_context';
import FavoriteToast from '@reverbdotcom/commons/src/components/favorite_toast';
import { ButtonVariant } from '@reverbdotcom/cadence/components/RCButton/styling';
import { FOLLOW_PROMPT_PARAM } from '../shared/constants';
import { hasNoFiltersApplied } from './find_favorite_context';

interface ExternalProps {
  shop: FavoriteShopCTAData.Fragment;
  initialFavoriteId: string | null;
  theme?: ButtonVariant;
  parentComponentName?: string;
}
interface ApolloProps {
  upsertMyFavoriteShop: MutationFn<UpsertMyFavoriteShop.Mutation, UpsertMyFavoriteShop.Variables>;
  deleteMyFavoriteShop: MutationFn<DeleteMyFavoriteShop.Mutation, DeleteMyFavoriteShop.Variables>;
}

type IProps = ExternalProps & ApolloProps;

export const FavoriteShopCTAFragment = gql`
  fragment FavoriteShopCTAData on Shop {
    _id
    id
    name
    address {
      countryCode
    }
    user {
      _id
      feedbackSummary {
        rollingRatingPercentage
        receivedCount
      }
    }
  }
`;

export function FavoriteShopCTA(props: IProps) {
  const [loading, setLoading] = useState<boolean>(false);
  const [toastOpen, setToastOpen] = useState<boolean>(false);
  const [upsertFavoriteShop] = useMutation(props.upsertMyFavoriteShop);
  const [deleteFavoriteShop] = useMutation(props.deleteMyFavoriteShop);

  const initialFavorite = {
    id: props.initialFavoriteId || '',
    title: '',
    subtitle: '',
    feedEnabled: false,
    emailEnabled: false,
    queryParams: '',
    favorited: !!props.initialFavoriteId,
  };
  const [favorite, setFavorite] = useState<core_apimessages_Favorite>(initialFavorite);

  const url = useURL();
  const showToast = () => setToastOpen(true);
  const hideToast = () => setToastOpen(false);
  const favoriteEventProperties = {
    eventName: MParticleEventName.ClickedToastMessage,
    currentView: resolveCurrentView(url),
    favoriteType: resolveFavoriteType(url),
  };

  const handleToastClose = (closeType: ToastCloseType) => {
    hideToast();

    if (closeType === 'dismiss' || closeType === 'swipe') {
      trackEvent(favoriteEventProperties);
    }
  };

  const user = useUser();

  if (user.loggedOut || !inExperiment(user, experiments.FAVORITES)) { return null; }

  const buildShopEventProps = () => {
    const starRating = getStarsCount(props.shop.user.feedbackSummary.rollingRatingPercentage / 100);

    return {
      id: props.shop.id,
      name: props.shop.name,
      country: props.shop.address.countryCode,
      rating: starRating.displayRating,
      parentComponentName: props.parentComponentName,
    };
  };

  const handleUndoFavoriteRemove = (result: MutationResult<UndoDeleteMyFavorite.Mutation>) => {
    setFavorite(result.data.undoDeleteMyFavorite?.favorite);
  };

  const shouldAutoFavoriteShop = () => {
    if (!url.query[FOLLOW_PROMPT_PARAM]) { return false; }
    if (favorite.id) { return false; }
    if (loading) { return false; }

    return hasNoFiltersApplied(url.query);
  };

  if (shouldAutoFavoriteShop()) {
    handleFavorite();
  }

  async function handleFavorite() {
    try {
      setLoading(true);

      if (favorite.favorited) {
        const result = await deleteFavoriteShop({
          variables: {
            input: {
              id: favorite.id,
            },
          },
        });

        setFavorite(result?.data.deleteMyFavorite?.favorite);
        setLoading(false);
        showToast();
        trackEvent({
          eventName: MParticleEventName.ClickedRemoveFavoriteShop,
          ...buildShopEventProps(),
        });
      } else {
        const result = await upsertFavoriteShop({
          variables: {
            input: {
              feedEnabled: false,
              emailEnabled: false,
              shopId: parseInt(props.shop.id),
            },
          },
        });

        setFavorite(result?.data.upsertMyFavorite?.favorite);
        setLoading(false);
        showToast();
        trackEvent({
          eventName: MParticleEventName.ClickedAddFavoriteShop,
          ...buildShopEventProps(),
        });
      }
    } catch (e) {
      elog.error(
        e.message,
        { componentName: 'FavoriteShopCTA' },
        e,
      );
    }
  }

  const classes = classNames(
    'favorite-shop-cta',
    {
      'favorite-shop-cta--favorited': favorite.favorited,
    },
  );

  return (
    <span className={classes}>
      <RCButton
        variant={props.theme || 'secondary'}
        onClick={() => handleFavorite()}
        disabled={loading}
        fullWidth
      >
        <RCTextWithIcon
          svgComponent={favorite.favorited ? HeartFilledIcon : HeartEmptyIcon}
          placement="left"
        >
          {favorite.favorited ? I18n.t('discovery.favorites.cta.savedShop') : I18n.t('discovery.favorites.cta.saveShop')}
        </RCTextWithIcon>
      </RCButton>

      <FavoriteToast
        favoriteId={favorite.id}
        isFavorited={favorite.favorited}
        favoriteType={core_apimessages_FavoriteType.FAVORITE_SHOP}
        isOpen={toastOpen}
        handleToastClose={handleToastClose}
        handleMutationResult={handleUndoFavoriteRemove}
      />
    </span>
  );
}

const withUpsertFavoriteShopMutation =
  withGraphql<ExternalProps, UpsertMyFavoriteShop.Mutation, UpsertMyFavoriteShop.Variables>(
    gql`
      mutation UpsertMyFavoriteShop($input: Input_core_apimessages_UpsertMyFavoriteRequest) {
        upsertMyFavorite(input: $input) {
          favorite {
            id
            favorited
            searchableId
            searchableType
            queryParams
          }
        }
      }
    `,
    {
      name: 'upsertMyFavoriteShop',
    },
  );

const withDeleteFavoriteShopMutation =
  withGraphql<ExternalProps, DeleteMyFavoriteShop.Mutation, DeleteMyFavoriteShop.Variables>(
    gql`
      mutation DeleteMyFavoriteShop($input: Input_core_apimessages_DeleteMyFavoriteRequest) {
        deleteMyFavorite(input: $input) {
          favorite {
            id
            favorited
          }
        }
      }
    `,
    {
      name: 'deleteMyFavoriteShop',
    },
  );

export default connect<ExternalProps>([
  withUpsertFavoriteShopMutation,
  withDeleteFavoriteShopMutation,
])(FavoriteShopCTA);
